import { ActionTree, GetterTree, MutationTree } from 'vuex';

import store from '@/store';
import StartGuideService from '@/api/StartGuideService';
import {
  EStartGuideDiscoverMoreSlug,
  EStartGuideTaskSlug,
  StartGuideCardActiveProgram,
  StartGuideDiscoverMoreCard,
  StartGuideCardNeedHelp,
  StartGuidePreferences,
  StartGuideState,
  StartGuideTask
} from '@/models/start-guide';
import { IRouterProps } from '@/router/interfaces/router-props.interface';

import { IDropDownCardMenu } from '@/models/dropdownCardMenu';
import { IBusinessType } from '@/models/business';
import BusinessService from '@/api/BusinessService';

import { RootState } from '../types';
import Module from '../interfaces/module.interface';
import DateUtils from '@/utils/DateUtils';
import flagKeys from '@/constants/flagKeys';
import segmentIo from '@/utils/segment.io';
import {
  getReadableStartGuideType,
  getTaskReadableName
} from '@/utils/start-guide';
import isRemoteLoading from '@/utils/remote-loading';

const startGuideService = new StartGuideService();

export const triggerBySlug: Record<string, string> = {
  [EStartGuideTaskSlug.REWARD_COINS]: 'link:program-status',
  [EStartGuideTaskSlug.SELL_COINS]: 'link:sell_coins',
  [EStartGuideTaskSlug.CUSTOMIZE_COIN]: 'link:coin_image',
  [EStartGuideTaskSlug.SOCIAL_REWARDS]: 'link:social-rewards',
  [EStartGuideTaskSlug.REFERRAL_PROGRAM]: 'link:referral-program',
  [EStartGuideTaskSlug.SPECIAL_COINS]: 'link:special-coins-offers',
  [EStartGuideTaskSlug.INVITE_CUSTOMERS]: 'link:customer-import',
  [EStartGuideTaskSlug.WELCOME_COINS]: 'link:offer-welcome-coins'
};

export const actionButtonsActivePrograms: Record<
  string,
  IDropDownCardMenu[]
> = {
  [EStartGuideTaskSlug.SELL_COINS]: [
    {
      trigger: 'modal:disableSellCoins',
      label: 'Deactivate',
      key: `${EStartGuideTaskSlug.SELL_COINS}_deactivate`
    }
  ],
  [EStartGuideTaskSlug.REWARD_COINS]: [
    {
      trigger: 'link:program-status',
      label: 'Program status',
      key: `${EStartGuideTaskSlug.REWARD_COINS}_program_status`,
      flag: flagKeys.loyaltyProgramStatus
    },
    {
      trigger: 'link:coin/loyalty',
      label: 'Edit settings',
      key: `${EStartGuideTaskSlug.REWARD_COINS}_edit_settings`
    }
  ],
  [EStartGuideTaskSlug.WELCOME_COINS]: [
    {
      trigger: 'method:deactivate',
      label: 'Deactivate',
      key: `${EStartGuideTaskSlug.WELCOME_COINS}_deactivate`
    }
  ],
  [EStartGuideTaskSlug.SPECIAL_COINS]: [
    {
      trigger: 'link:special-coins-offers',
      label: 'View all Offers',
      key: `${EStartGuideTaskSlug.SPECIAL_COINS}_view_all_offers`
    }
  ],
  [EStartGuideTaskSlug.REFERRAL_PROGRAM]: [
    {
      trigger: 'method:deactivate',
      label: 'Deactivate',
      key: `${EStartGuideTaskSlug.REFERRAL_PROGRAM}_deactivate`
    }
  ],
  [EStartGuideTaskSlug.INVITE_CUSTOMERS]: [
    {
      trigger: 'modal:disableCampaign',
      label: 'Deactivate',
      key: `${EStartGuideTaskSlug.INVITE_CUSTOMERS}_deactivate`
    }
  ],
  [EStartGuideTaskSlug.SOCIAL_REWARDS]: [
    {
      trigger: 'link:social-rewards',
      label: 'Edit social rewards',
      key: `${EStartGuideTaskSlug.SOCIAL_REWARDS}_view_all_offers`
    }
  ]
};

export const actionButtonsDiscoverMore: Record<string, IDropDownCardMenu[]> = {
  [EStartGuideDiscoverMoreSlug.TELL_US_MORE]: [
    {
      trigger: 'method:deactivate',
      label: 'Deactivate',
      key: `${EStartGuideDiscoverMoreSlug.TELL_US_MORE}_deactivate`
    }
  ],
  [EStartGuideDiscoverMoreSlug.ADD_LOCATIONS]: [
    {
      trigger: 'method:deactivate',
      label: 'Deactivate',
      key: `${EStartGuideDiscoverMoreSlug.ADD_LOCATIONS}_deactivate`
    }
  ],
  [EStartGuideDiscoverMoreSlug.ADD_MEMBERS]: [
    {
      trigger: 'method:deactivate',
      label: 'Deactivate',
      key: `${EStartGuideDiscoverMoreSlug.ADD_MEMBERS}_deactivate`
    }
  ]
};

export function setTriggerOnTasks(tasks: StartGuideTask[]) {
  return tasks.map(task => ({
    ...task,
    trigger: triggerBySlug[task.slug]
  }));
}

export function filterTasksBy(operationType: IBusinessType) {
  return (task: StartGuideTask) => task.availableFor.includes(operationType);
}

export function setPropertiesCardActivePrograms(
  programs: StartGuideCardActiveProgram[]
) {
  return programs.map(program => ({
    ...program,
    menuLinks: actionButtonsActivePrograms[program.slug] || null,
    link: setLinkActivePrograms(program)
  }));
}

export function setPropertiesDiscoverMore(
  dicovers: StartGuideDiscoverMoreCard[]
) {
  return dicovers.map(discover => ({
    ...discover,
    menuLinks: actionButtonsDiscoverMore[discover.slug] || null
  }));
}

export function setLinkActivePrograms(program: StartGuideCardActiveProgram) {
  if (program.slug === 'welcome-coins' && program.createdAt) {
    const createdAt = DateUtils.formattedDate({
      date: program.createdAt,
      applyTimezone: true,
      format: 'YYYY-MM-DD'
    });

    const expiredAt = DateUtils.formattedDate({
      date: program.expiredDate ? program.expiredDate : new Date(),
      applyTimezone: true,
      format: 'YYYY-MM-DD'
    });

    return `marketing-center/offer-free-coins/${
      program.id
    }?from=${createdAt}&to=${expiredAt}`;
  }
  return program.link;
}

const initialState = (): StartGuideState => ({
  completedAllTasks: false,
  loading: false,
  preferences: {
    expanded: true,
    showTipCoinMessage: true
  },
  tasks: [],
  activePrograms: [],
  discoverMore: [],
  needHelp: [],
  homeOverview: {}
});

const businessService = new BusinessService();

const factory = async (context: IRouterProps) => {
  const supportedAuths = new Set(['manager']);

  if (
    !supportedAuths.has(context.auth) ||
    !context.routeRequirements.managerHaveBusiness
  )
    return;

  const flags = context.store.getters['flags/getFlags'];

  // FIXME: business request being made twice (as fallback), ideally the context should never return undefined
  const businessInfo =
    context.store.getters['business/getBusinessInfo'].business ||
    (await businessService.getBusinessInfo()).data;

  const loadTasks = async () => {
    context.store.dispatch('startGuide/SET_LOADING', true);

    const { status, data } = await startGuideService.getTasks();

    context.store.dispatch('startGuide/SET_LOADING', false);

    if (status !== 200 || !data || !data.items) return;

    const { preferences, tasks, programs } = data.items;

    let transformedTasks = setTriggerOnTasks(tasks);

    transformedTasks = transformedTasks.filter(
      filterTasksBy(businessInfo.operationType as IBusinessType)
    );

    const transformedPrograms = setPropertiesCardActivePrograms(programs);

    const wasCompletedAlltasks = transformedTasks.every(
      task => task.completed || task.dismissed
    );

    context.store.dispatch(
      'startGuide/SET_COMPLETED_ALL_TASKS',
      wasCompletedAlltasks
    );

    context.store.dispatch('startGuide/SET_TASKS', transformedTasks);
    context.store.dispatch(
      'startGuide/SET_ACTIVE_PROGRAMS',
      transformedPrograms
    );
    context.store.dispatch('startGuide/SET_PREFERENCES', preferences);

    if (transformedTasks.every(task => Boolean(task.completed))) {
      context.store.dispatch('startGuide/ADD_SUCCESS_TASK', data);
    }
  };

  if (businessInfo && !isRemoteLoading()) {
    await loadTasks();
  }
};

const getters: GetterTree<StartGuideState, RootState> = {
  getLoading: state => state.loading,
  getCompletedAllTasks: state => state.completedAllTasks,
  getTasks: state =>
    state.tasks.filter((task: StartGuideTask) => !task.dismissed),
  getActivePrograms: state => state.activePrograms,
  getDiscoverMore: state => state.discoverMore,
  getNeedHelp: state => state.needHelp,
  getPreferences: state => state.preferences,
  getHomeOverview: state => state.homeOverview
};

const actions: ActionTree<StartGuideState, RootState> = {
  RESET_START_GUIDE({ commit }) {
    commit('resetStartGuide');
  },
  SET_COMPLETED_ALL_TASKS({ commit }, payload: boolean) {
    commit('setCompletedAlltasks', payload);
  },
  FILTER_TASKS_BY_OPERATION({ commit }, payload: IBusinessType) {
    commit('filterTasksByOperation', payload);
  },
  SET_LOADING({ commit }, payload: boolean) {
    commit('setLoading', payload);
  },
  SET_TASKS({ commit }, payload: StartGuideTask[]) {
    commit(
      'setTasks',
      payload.filter((task: StartGuideTask) => !task.dismissed)
    );
  },
  SET_PREFERENCES({ commit }, payload: Partial<StartGuidePreferences>) {
    commit('setPreferences', payload);
  },
  SET_HOME_OVERVIEW({ commit }, payload: Record<string, any>) {
    commit('setHomeOverview', payload);
  },
  ADD_TASK({ commit }, payload: StartGuideTask) {
    commit('addTask', payload);
  },
  ADD_SUCCESS_TASK({ commit }) {
    commit('addSuccessTask');
  },
  COMPLETED_TASK_TOGGLE(
    { commit },
    payload: { slug: string; dismissed: boolean }
  ) {
    if (payload.dismissed) {
      segmentIo.track('Start Guide Step Skipped', {
        Feature: getTaskReadableName(payload.slug),
        'Start Guide Type': getReadableStartGuideType()
      });
    } else {
      segmentIo.track('Start Guide Step Completed', {
        Feature: getTaskReadableName(payload.slug),
        'Start Guide Type': getReadableStartGuideType()
      });
    }

    commit('completedTaskToggle', payload);
  },
  GET_NEED_HELP({ commit }) {
    commit('setLoading', true);

    startGuideService
      .getNeedHelp()
      .then(response => {
        commit('setNeedHelp', response);
      })
      .finally(() => commit('setLoading', false));
  },
  SET_ACTIVE_PROGRAMS({ commit }, payload: StartGuideCardActiveProgram[]) {
    commit('setActivePrograms', payload);
  },
  SET_NEED_HELP({ commit }, payload: StartGuideCardNeedHelp[]) {
    commit('setNeedHelp', payload);
  },
  GET_TASKS({ commit, rootState }, callback?: () => void) {
    commit('setLoading', true);

    startGuideService
      .getTasks()
      .then(({ data }) => {
        let preferences = data.items.preferences;
        let tasks = setTriggerOnTasks(data.items.tasks);

        const businessInfo = (rootState as any).business.business || {};
        if (businessInfo.operationType)
          tasks = tasks.filter(
            filterTasksBy(businessInfo.operationType as IBusinessType)
          );
        const programs = setPropertiesCardActivePrograms(data.items.programs);

        commit('setActivePrograms', programs);
        commit('setPreferences', preferences);
        commit('setTasks', tasks);
        callback && callback();
      })
      .finally(() => commit('setLoading', false));
  },
  UPDATE_TASKS({ commit, state, dispatch, rootState }, slug?: string) {
    commit('setLoading', true);

    const updatedTask = state.tasks.find(task => task.slug === slug);
    if (!updatedTask) {
      commit('setLoading', false);
      return;
    }

    startGuideService
      .updateTask(updatedTask)
      .then(({ data }) => {
        let tasks = data.items.tasks.filter(
          (task: StartGuideTask) => !task.dismissed
        );

        tasks = setTriggerOnTasks(tasks);

        const businessInfo = (rootState as any).business.business;

        if (businessInfo.operationType) {
          tasks = tasks.filter(
            filterTasksBy(businessInfo.operationType as IBusinessType)
          );
        }

        commit('setTasks', tasks);

        const programs = setPropertiesCardActivePrograms(data.items.programs);

        commit('setActivePrograms', programs);

        if (tasks.every((task: StartGuideTask) => task.completed)) {
          segmentIo.track('Start Guide Finished', {
            'Start Guide Type': getReadableStartGuideType()
          });

          commit('addSuccessTask');
        }
      })
      .finally(() => commit('setLoading', false));
  },
  UPDATE_PREFERENCES({ commit, state, rootState }) {
    commit('setLoading', true);

    startGuideService
      .updatePreferences(state.preferences)
      .then(async ({ data }) => {
        commit('setPreferences', data);
      })
      .finally(() => commit('setLoading', false));
  },
  GET_DISCOVER_MORE({ commit }) {
    commit('setLoading', true);
    startGuideService
      .getDiscoverMore()
      .then(response => {
        commit('setDiscoverMore', response);
      })
      .finally(() => commit('setLoading', false));
  }
};

const mutations: MutationTree<StartGuideState> = {
  resetStartGuide(state) {
    Object.assign(state, initialState());
  },
  setCompletedAlltasks(state, wasCompletedAllTasks: boolean) {
    state.completedAllTasks = wasCompletedAllTasks;
  },
  filterTasksByOperation(state, operationType: IBusinessType) {
    state.tasks = state.tasks.filter(filterTasksBy(operationType));
  },
  setTasks(state, tasks: StartGuideTask[]) {
    state.tasks = tasks;
  },
  setHomeOverview(state, overview: Record<string, any>) {
    state.homeOverview = overview;
  },
  setActivePrograms(state, activePrograms: StartGuideCardActiveProgram[]) {
    state.activePrograms = activePrograms;
  },
  setNeedHelp(state, needHelp: StartGuideCardNeedHelp[]) {
    state.needHelp = needHelp;
  },
  addTask(state, task: StartGuideTask) {
    state.tasks = [...state.tasks, task];
  },
  addSuccessTask(state) {
    state.tasks = [
      ...state.tasks,
      {
        slug: EStartGuideTaskSlug.SUCCESS_MESSAGE,
        type: 'feedback',
        title: 'Nice work! 🎉',
        description:
          'You’re all set up! Don’t forget to spread the word about your new campaigns. Explore what else you can do with Twism.',
        actionLabel: 'Discover more',
        trigger: 'scrollAt:start-guide-discover-more-section',
        availableFor: []
      }
    ];
  },
  setLoading(state, isLoading: boolean) {
    state.loading = isLoading;
  },
  completedTaskToggle(state, payload: { slug: string; dismissed: boolean }) {
    state.tasks = state.tasks.map(currTask => ({
      ...currTask,
      ...(currTask.slug === payload.slug && {
        completed: !currTask.completed,
        dismissed: payload.dismissed
      })
    }));
  },
  setPreferences(state, preferences: Partial<StartGuidePreferences>) {
    state.preferences = {
      ...state.preferences,
      ...preferences
    };
  },
  setDiscoverMore(state, discoverMore: StartGuideDiscoverMoreCard[]) {
    state.discoverMore = discoverMore;
  }
};

export const startGuide: Module<StartGuideState, RootState> = {
  namespaced: true,
  initializer: {
    factory,
    requires: ['BusinessStore'],
    provide: 'StartGuideStore',
    on: 'BeforeEachRoute'
  },
  state: initialState(),
  getters,
  actions,
  mutations
};
